import React, { useState, useEffect } from "react";
import { ActionButton, Modal, FormActions, Input, Select, IconsLarge } from "../../shared";
import type { SelectOption } from "../../shared";
import { transactionsApi } from "../../../services";
import type { BankAccountAttributes } from "../../../types";
import type { CostCenterAttributes } from "../../../types";
import {
  competenceDateToMonthValue,
  monthValueToCompetenceApiDate,
} from "../../../utils/competenceDateUtils";

const PAYMENT_METHOD_OPTIONS: SelectOption[] = [
  { value: "", label: "Nenhum" },
  { value: "Dinheiro", label: "Dinheiro" },
  { value: "Pix", label: "Pix" },
  { value: "Cartão de Crédito", label: "Cartão de Crédito" },
  { value: "Cartão de Débito", label: "Cartão de Débito" },
];

export interface CreateTransferTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: number;
  bankAccounts: BankAccountAttributes[];
  costCenters: CostCenterAttributes[];
}

export const CreateTransferTransactionModal: React.FC<
  CreateTransferTransactionModalProps
> = ({ isOpen, onClose, onSuccess, companyId, bankAccounts, costCenters }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [originBankAccountId, setOriginBankAccountId] = useState<number | "">("");
  const [destinationBankAccountId, setDestinationBankAccountId] = useState<number | "">("");
  const [costCenterId, setCostCenterId] = useState<number | "">("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [competenceDate, setCompetenceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [observation, setObservation] = useState("");
  const [product, setProduct] = useState("");
  const [client, setClient] = useState("");
  const [extraFieldsExpanded, setExtraFieldsExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setOriginBankAccountId("");
    setDestinationBankAccountId("");
    setCostCenterId("");
    setAmount("");
    setPaymentMethod("");
    setCompetenceDate("");
    setDueDate("");
    setPaymentDate("");
    setObservation("");
    setProduct("");
    setClient("");
    setExtraFieldsExpanded(false);
  }, [isOpen]);

  const bankOptions: SelectOption[] = bankAccounts.map((a) => ({
    value: a.id,
    label: a.name,
  }));

  const destinationOptions: SelectOption[] =
    originBankAccountId !== "" && typeof originBankAccountId === "number"
      ? bankOptions.filter((o) => o.value !== originBankAccountId)
      : bankOptions;

  const costCenterOptions: SelectOption[] = costCenters.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const originName =
    typeof originBankAccountId === "number"
      ? bankAccounts.find((b) => b.id === originBankAccountId)?.name ?? ""
      : "";
  const destName =
    typeof destinationBankAccountId === "number"
      ? bankAccounts.find((b) => b.id === destinationBankAccountId)?.name ?? ""
      : "";
  const descriptionPreview =
    originName && destName ? `Transferência - ${originName} - ${destName}` : "—";

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    const originId =
      typeof originBankAccountId === "number" ? originBankAccountId : Number(originBankAccountId);
    const destId =
      typeof destinationBankAccountId === "number"
        ? destinationBankAccountId
        : Number(destinationBankAccountId);
    const costId =
      typeof costCenterId === "number" ? costCenterId : costCenterId === "" ? NaN : Number(costCenterId);
    const amountNum = parseFloat(amount);

    if (!originId || Number.isNaN(originId)) {
      setError("Selecione a conta de origem.");
      return;
    }
    if (!destId || Number.isNaN(destId)) {
      setError("Selecione a conta de destino.");
      return;
    }
    if (originId === destId) {
      setError("A conta de origem e a de destino devem ser diferentes.");
      return;
    }
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      setError("Informe um valor maior que zero.");
      return;
    }

    const origin = bankAccounts.find((b) => b.id === originId);
    const dest = bankAccounts.find((b) => b.id === destId);
    const description = `Transferência - ${origin?.name ?? "?"} - ${dest?.name ?? "?"}`;

    const costCenterValue = costId && !Number.isNaN(costId) ? costId : null;

    setLoading(true);
    setError(null);
    try {
      const res = await transactionsApi.transfer({
        companyId,
        amount: amountNum,
        originBankAccountId: originId,
        destinationBankAccountId: destId,
        paymentMethod: paymentMethod.trim() || null,
        description,
        observation: observation.trim() || null,
        competenceDate: competenceDate.trim() || null,
        dueDate: dueDate.trim() || null,
        paymentDate: paymentDate.trim() || null,
        product: product.trim() || null,
        client: client.trim() || null,
        costCenterId: costCenterValue,
      });
      if (!res.success) {
        setError(res.error ?? "Falha ao registrar a transferência.");
        setLoading(false);
        return;
      }

      onSuccess();
      onClose();
    } catch {
      setError("Falha ao registrar a transferência.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nova transferência"
      icon={<IconsLarge.BankAccount />}
      size="lg"
      headerGradient="blue"
    >
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3 mb-6">
          <span className="text-xl">⚠️</span>
          <span className="text-red-800 dark:text-red-200 font-medium">{error}</span>
        </div>
      )}

      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
        <p className="text-sm text-gray-600 dark:text-slate-400">
          Serão criados dois lançamentos: uma <strong>saída</strong> na conta de origem e uma{" "}
          <strong>entrada</strong> na conta de destino, com o mesmo valor e demais dados. A
          categoria é definida pelo sistema.
        </p>

        <div className="rounded-xl border border-gray-200 dark:border-[#374151] bg-gray-50/80 dark:bg-[#1A202C]/50 px-3 py-2 text-sm">
          <span className="text-gray-500 dark:text-slate-500">Descrição gerada: </span>
          <span className="font-medium text-gray-900 dark:text-slate-200">{descriptionPreview}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Conta de origem (saída)"
            value={originBankAccountId}
            onChange={(v) => {
              setOriginBankAccountId(Number(v));
              if (destinationBankAccountId === Number(v)) setDestinationBankAccountId("");
            }}
            options={bankOptions}
            placeholder={bankAccounts.length ? "Selecione" : "Nenhuma conta"}
            disabled={loading || bankAccounts.length === 0}
            required
            searchable={bankOptions.length > 6}
          />
          <Select
            label="Conta de destino (entrada)"
            value={destinationBankAccountId}
            onChange={(v) => setDestinationBankAccountId(Number(v))}
            options={destinationOptions}
            placeholder={bankAccounts.length ? "Selecione" : "Nenhuma conta"}
            disabled={loading || bankAccounts.length === 0}
            required
            searchable={destinationOptions.length > 6}
          />
          <Select
            label="Centro de custo"
            value={costCenterId}
            onChange={(v) => setCostCenterId(v === "" ? "" : Number(v))}
            options={[{ value: "", label: "Nenhum" }, ...costCenterOptions]}
            placeholder="Opcional"
            disabled={loading}
            searchable={costCenterOptions.length > 6}
          />
          <Input
            label="Valor (R$)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step={0.01}
            disabled={loading}
            required
            placeholder="0,00"
          />
          <Select
            label="Forma de pagamento"
            value={paymentMethod}
            onChange={(v) => setPaymentMethod(String(v))}
            options={PAYMENT_METHOD_OPTIONS}
            placeholder="Selecione"
            disabled={loading}
            searchable={false}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input
            label="Competência (mês/ano)"
            type="month"
            value={competenceDateToMonthValue(competenceDate)}
            onChange={(e) => setCompetenceDate(monthValueToCompetenceApiDate(e.target.value))}
            disabled={loading}
          />
          <Input
            label="Vencimento"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={loading}
          />
          <Input
            label="Data pagamento"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="border border-gray-200 dark:border-[#374151] rounded-xl overflow-hidden">
          <ActionButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExtraFieldsExpanded((e) => !e)}
            className="w-full !rounded-none border-0 shadow-none justify-between px-4 py-3 text-left text-sm bg-gray-50 dark:bg-[#1A202C]/80 hover:bg-gray-100 dark:hover:bg-[#374151] text-gray-700 dark:text-slate-300 font-medium"
          >
            <span>Observação, produto e cliente</span>
            <span className="text-gray-500 dark:text-slate-400 shrink-0" aria-hidden>
              {extraFieldsExpanded ? "▲ Recolher" : "▼ Expandir"}
            </span>
          </ActionButton>
          {extraFieldsExpanded && (
            <div className="p-4 space-y-4 border-t border-gray-200 dark:border-[#374151] bg-white dark:bg-[#2D3748]/50">
              <Input
                label="Observação"
                type="text"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                disabled={loading}
                placeholder="Observações"
              />
              <Input
                label="Produto ou serviço"
                type="text"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                disabled={loading}
                placeholder="Produto ou serviço"
              />
              <Input
                label="Cliente ou fornecedor"
                type="text"
                value={client}
                onChange={(e) => setClient(e.target.value)}
                disabled={loading}
                placeholder="Cliente ou fornecedor"
              />
            </div>
          )}
        </div>

        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          isEditing={false}
          submitType="button"
          submitText="Registrar transferência"
        />
      </div>
    </Modal>
  );
};
